import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

/**
 * PageTransition — lightweight CSS-based fade/slide on route changes.
 *
 * Uses a `key` derived from `location.pathname` to remount content, triggering
 * the `page-enter` keyframe defined in component-overrides. Respects
 * `prefers-reduced-motion` (the keyframe is neutralized globally by CssBaseline).
 *
 * No external animation library is required.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [visible, setVisible] = useState(true);
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      setVisible(false);
      prevPath.current = location.pathname;
      // Defer to next frame so the exit→enter swap triggers the CSS animation
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [location.pathname]);

  return (
    <div
      key={location.pathname}
      style={{
        animation: visible
          ? `page-enter ${'200ms'} cubic-bezier(0.4, 0, 0.2, 1)`
          : 'none',
      }}
    >
      {children}
    </div>
  );
}
